/**
 * useAutoEndRecording
 *
 * The egress bot records the class as a LiveKit *web* egress — it keeps
 * recording the page until something calls StopEgress. If the tutor and every
 * student leave but the egress is never stopped (a missed class-end flow, a
 * crashed tab, a class that just fizzles out), we burn an egress slot and
 * upload an empty room for as long as it stays up.
 *
 * This watches the room from inside the recorder and, once the class has
 * actually had people in it, stops the recording after it has been empty of
 * tutors/students for a sustained window (default 5 min). The stop goes through
 * the same lms-backend endpoint the tutor "stop" and class-end flows use, which
 * resolves the active egress by classId — so the recorder only needs classId.
 *
 * Must be rendered inside a LiveKitRoom (uses useRemoteParticipants).
 */

import { useEffect, useRef, type FC } from 'react';
import { useConnectionState, useRemoteParticipants } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { datadogLogs } from '@datadog/browser-logs';

import apiService from './apiService';
import { PRESET_TYPE } from 'configs/meeting';
import { getPresetFromMetadata } from 'adapters/LiveKitMeetingAdapter';

interface UseAutoEndRecordingArgs {
  classId: string | null;
  enabled: boolean;
  emptyThresholdMs: number;
  /**
   * The LiveKit recorder token this page was launched with. Forwarded as a
   * Bearer credential so the stop endpoint can authorize the recorder by the
   * token scoped to this class — its only credential for the stop.
   */
  livekitToken?: string;
  /**
   * Set once useLiveKitReconnect has spent its retry budget. The room is never
   * coming back, so we stop holding the countdown and let the watchdog free the
   * egress slot. See the connection gate below.
   */
  reconnectExhausted?: boolean;
}

/**
 * A "human" is a tutor or student. The recorder itself is the local
 * participant (never in useRemoteParticipants), and viewers / other recorders /
 * agents join as VIEWER — so they don't keep an otherwise-empty class alive.
 * Unknown metadata falls back to STUDENT (see getPresetFromMetadata), i.e. we
 * err towards "still occupied" and never end early on a mislabelled join.
 */
const isHuman = (metadata?: string): boolean => {
  const preset = getPresetFromMetadata(metadata);
  return preset === PRESET_TYPE.TUTOR || preset === PRESET_TYPE.STUDENT;
};

export const useAutoEndRecording = ({
  classId,
  enabled,
  emptyThresholdMs,
  livekitToken,
  reconnectExhausted = false,
}: UseAutoEndRecordingArgs): void => {
  const remoteParticipants = useRemoteParticipants();
  const humanCount = remoteParticipants.filter((p) => isHuman(p.metadata)).length;
  const connected = useConnectionState() === ConnectionState.Connected;

  // The class has actually started (someone joined at least once). Until then
  // the room is "empty" only because the recorder joined ahead of everyone —
  // we must NOT arm the timer during the pre-class wait, or a late tutor loses
  // the recording.
  const hasSeenHumanRef = useRef(false);
  // Set once we've fired the stop, so we never double-stop.
  const endedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Always holds the latest committed occupancy, so the fired timer can
  // re-check it at the moment it runs (not the value captured when it armed).
  const humanCountRef = useRef(humanCount);
  useEffect(() => {
    humanCountRef.current = humanCount;
  }, [humanCount]);

  useEffect(() => {
    if (!enabled || !classId) return;

    // Active-flag: the cleanup flips this, and the (async) timer callback
    // re-checks it before stopping — so a participant returning during the
    // countdown, or even after the timer fires, cancels the stop.
    let cancelled = false;

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    // A dropped room reports zero remote participants — that means "we can't
    // see the class", not "the class is empty". Without this gate a transport
    // failure (e.g. CONNECTION_TIMEOUT) would stop the egress a few minutes
    // later, turning a recoverable blip into a truncated recording. Hold the
    // countdown while we're offline; useLiveKitReconnect is dialling back.
    // Once it has given up the page really is dead, so we let the countdown run
    // rather than leave the egress recording a frozen frame until class-end.
    if (!connected && !reconnectExhausted) {
      clearTimer();
      return;
    }

    if (humanCount > 0) {
      // Occupied: mark started and cancel any pending countdown.
      hasSeenHumanRef.current = true;
      clearTimer();
      return;
    }

    // Empty. Don't arm before the class has started, after we've already
    // stopped, or if a countdown is already running.
    if (!hasSeenHumanRef.current || endedRef.current || timerRef.current) return;

    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      // Bail if this countdown was cancelled (a participant returned and the
      // effect cleaned up), if the room is occupied again by the latest count,
      // or if we've already stopped.
      if (cancelled || endedRef.current || humanCountRef.current > 0) return;
      endedRef.current = true;
      try {
        // Recorder-scoped stop route: authorized by the class LiveKit token,
        // not a user session. See lms-backend recording-egress.
        await apiService.put(
          `/api/v2/recording-egress/${classId}/livekit/stop`,
          undefined,
          livekitToken
            ? { headers: { Authorization: `Bearer ${livekitToken}` } }
            : undefined,
        );
        datadogLogs.logger.info(
          'auto-ended recording: room empty of tutor/student past threshold',
          {
            label: 'auto-end-recording',
            source: 'recording-app',
            classId,
            emptyThresholdMs,
          },
        );
      } catch (error) {
        // Leave endedRef false so a still-empty room retries on the next
        // participant change. The class-end flow remains the backstop.
        endedRef.current = false;
        datadogLogs.logger.error(
          'auto-end recording failed to stop egress',
          {
            label: 'auto-end-recording',
            source: 'recording-app',
            classId,
            error: (error as Error)?.message,
          },
          error as Error,
        );
      }
    }, emptyThresholdMs);

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [
    humanCount,
    connected,
    reconnectExhausted,
    enabled,
    classId,
    emptyThresholdMs,
    livekitToken,
  ]);
};

/**
 * Headless controller — render inside <LiveKitRoom> to wire up the auto-end.
 * Renders nothing.
 */
const AutoEndRecording: FC<UseAutoEndRecordingArgs> = (props) => {
  useAutoEndRecording(props);
  return null;
};

export default AutoEndRecording;
